/**
 * `ModelRowReads` — a rows-side row as a sheet's reads (`SheetModelReads`)
 * hand it out: its identity, descriptor, metadata, tree position, cells, the
 * option controls it contributes and its sheet's reads, and no writer of any
 * kind.
 *
 * The object is a frozen forwarding face, one per row (`rowReadsOf`): a
 * plain object, not the live row (`ModelRow` or one of its variants), whose
 * every member forwards to that row, so a read always sees the current
 * model while no writer, cast or prototype reaches the row behind it. A
 * caller writes a row's cells through the checked facade verbs on the
 * `@tento/lona` side (`row.writableSingle()?.write(CellTarget.date(day), value, actor)`
 * on a `SheetRow.Handle`), never through a row read off a sheet's reads.
 *
 * A `Pick` over `ModelRow`, so it cannot drift from the class; the member
 * snapshot of the repository's hygiene suite pins it.
 *
 * A row's face is also its variant's reads (`RowKind`, `RowKind.of`):
 * a source row's face carries `SourceRowReads`, an alias row's
 * `AliasRowReads`, and so on — one face per row, whichever read hands it
 * out, and none carries a writer.
 */

import type { Option } from "../core/option.ts";
import type { ModelRow } from "./model-row.ts";
import { frozenCopy } from "../core/frozen-copy.ts";

/** A rows-side row's reads — the element type of `SheetModelReads.rootRows`. */
export type ModelRowReads =
  & Pick<
    ModelRow,
    | "id"
    | "descriptor"
    | "label"
    | "lookupKey"
    | "heightPx"
    | "attributes"
    | "attributesAs"
    | "capabilities"
    | "peekCells"
    | "optionContributions"
    | "sheetReads"
    | "defaultCollapsedInPropertyList"
    | "defaultProjection"
  >
  & {
    /** The row's parent, as its reads; unset for a root row. */
    readonly parent: Option<ModelRowReads>;
    /** The row's children in order, as their reads. */
    readonly children: readonly ModelRowReads[];
  };

/** The live row behind each face — `liveRowOf`'s lookup; a face is no `ModelRow`. */
const rowsByFace = new WeakMap<ModelRowReads, ModelRow>();

/**
 * Set once by the variants barrel (`installRowFaces`): a live row's frozen
 * reads face, its variant's reads included (`kindOfLiveRow` in
 * `./row-kind.ts`, which names the variants this module cannot).
 */
let faceOfLiveRow: Option<(row: ModelRow) => ModelRowReads> = null;

/**
 * Install the face builder behind `rowReadsOf`. Called once, by the variants
 * barrel (`./variants/index`), which every sheet loads: this module and the
 * base class name no subclass themselves.
 */
export function installRowFaces(faceOf: (row: ModelRow) => ModelRowReads): void {
  faceOfLiveRow ??= faceOf;
}

/**
 * Record `face` as the frozen reads face built for `row` (`faceRowOf`
 * answers it back). Called once per row, by `kindOfLiveRow`.
 */
export function recordFace(face: ModelRowReads, row: ModelRow): void {
  rowsByFace.set(face, row);
}

/**
 * `base` with `members` defined onto it — their getters stay getters, so a
 * face member reads the live row on every access. The variant faces
 * (`RowKind`) are built with it; `Object.defineProperties` answers the
 * object it was given, which from here on carries both member sets.
 */
export function withFaceMembers<B extends ModelRowReads, X extends object>(base: B, members: X): B & X;
export function withFaceMembers(base: object, members: object): object {
  return Object.defineProperties(base, Object.getOwnPropertyDescriptors(members));
}

/**
 * The frozen reads face of a live row — what a sheet's reads
 * (`sheetReadsOf`, `platformOf(handle).sheet`, `row.sheetReads`) and
 * `RowKind.of` hand out: an object forwarding exactly `ModelRowReads`'
 * members, and its variant's reads (`RowKind`), to `row`, so every read
 * answers from the live model and no writer is reachable from it — not
 * through a cast, an `instanceof` or a prototype (the face is a plain object,
 * no `ModelRow`). Built once per row: the same face on every read, so reads
 * compare by identity. `liveRowOf(face)` is the implementor door back to the
 * row.
 */
export function rowReadsOf(row: ModelRow): ModelRowReads {
  if (faceOfLiveRow == null) throw new Error("RowKind.of: read before the row variants loaded");
  return faceOfLiveRow(row);
}

/** A row's base face, unfrozen: `ModelRowReads`' members, forwarding to `row`. */
export function baseFaceOf(row: ModelRow): ModelRowReads {
  return {
    id: row.id,
    descriptor: row.descriptor,
    get label() {
      return row.label;
    },
    get lookupKey() {
      return row.lookupKey;
    },
    get heightPx() {
      return row.heightPx;
    },
    attributes: () => row.attributes(),
    attributesAs: (decode) => row.attributesAs(decode),
    capabilities: () => row.capabilities(),
    peekCells: (range) => Object.freeze(row.peekCells(range)),
    optionContributions: () => frozenCopy(row.optionContributions()),
    get sheetReads() {
      return row.sheetReads;
    },
    get defaultCollapsedInPropertyList() {
      return row.defaultCollapsedInPropertyList;
    },
    get defaultProjection() {
      return row.defaultProjection;
    },
    get parent(): Option<ModelRowReads> {
      const parent = row.parent;
      return parent == null ? null : rowReadsOf(parent);
    },
    get children(): readonly ModelRowReads[] {
      return Object.freeze(row.children.map(rowReadsOf));
    },
  };
}

/**
 * The live row a reads face (`rowReadsOf`) was built for, from the face's own
 * record; `null` for anything else (a live row is no face). `liveRowOf`
 * (`./model-row.ts`) answers through it: this module names `ModelRow` as a
 * type only, so the class and its faces load in one direction.
 */
export function faceRowOf(face: ModelRowReads): Option<ModelRow> {
  return rowsByFace.get(face) ?? null;
}
